import { jsxs, jsx } from "react/jsx-runtime";
import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useIMask } from "react-imask";
import { AppointmentState } from "../../../appointment-details/appointment-details.types.js";
import { toTimeString } from "../../../../../utils/time/time.js";
import { useDebouncedCallback } from "use-debounce";
import { useRooms } from "../../room-provider.js";
import { INPUT_DELAY } from "../appointment-status-select/appointment-status-select.constants.js";
import RoomSelect from "../room-select/room-select.js";
import { RootContainer, StatusText, LabelContainer, IconContainer, StatusLabel, LoadingCircle, RadioButton, InputGroup, StyledTextField } from "./appointment-status-radio.styles.js";
import "date-fns";
import "@mui/material";
import "../../../../../components/menu-item/menu-item.js";
import "@mui/material/MenuItem/index.js";
import "../../../../../constants/create-theme/create-theme.js";
import "@mui/material/styles";
import "../../../../../constants/colors/palette.js";
import "../../../../../themes/shadows/shadows.js";
import "../../../../../themes/typography/typography.js";
import "../../../../../themes/typography/utils/defaultVariantMapping/defaultVariantMapping.js";
import "../../../../../themes/typography/utils/typographyVariants/typographyVariants.js";
import "../../../../../themes/button/button.js";
import "../../../../../themes/button/styles/external/bordered.styles.js";
import "color-alpha";
import "../../../../../themes/button/styles/external/filled.styles.js";
import "../../../../../themes/button/styles/internal/bordered.styles.js";
import "../../../../../themes/button/styles/internal/borderless.styles.js";
import "../../../../../themes/button/styles/internal/filled.styles.js";
import "../../../../../themes/button/styles/internal/shadowed.styles.js";
import "../../../../../themes/checkbox/checkbox.js";
import "../../../../../themes/checkbox/components/CheckedIcon/CheckedIcon.js";
import "@mui/material/SvgIcon/index.js";
import "../../../../../themes/checkbox/components/IndeterminateIcon/IndeterminateIcon.js";
import "../../../../../themes/external-theme/external-theme.js";
import "../../../../../constants/breakpoints/breakpoints.js";
import "../../../../../themes/alert/alert.js";
import "../../../../../themes/alert/components/ErrorIcon/ErrorIcon.js";
import "../../../../../themes/alert/components/InfoIcon/InfoIcon.js";
import "../../../../../themes/alert/components/SuccessIcon/SuccessIcon.js";
import "../../../../../themes/alert/components/WarningIcon/WarningIcon.js";
import "../../../../../themes/form-control-label/form-control-label.js";
import "../../../../../themes/radio/radio.js";
import "../../../../../themes/switch/switch.js";
import "../../../../../themes/pickers-popper/pickers-popper.js";
import "../../../../../components/menu-item/components/menu-item-counter/menu-item-counter.js";
import "../../../../../components/menu-item/assets/icons/minus-circle.js";
import "../../../../../components/menu-item/assets/icons/plus-circle.js";
import "../../../../../components/menu-item/components/menu-item-counter/menu-item-counter.styles.js";
import "../../../../../components/menu-item/menu-item.styles.js";
import "../../../../../components/avatar/avatar.js";
import "@mui/material/Avatar/index.js";
import "../../../../../components/avatar/avatar.styles.js";
import "../../../../../components/icons/MainIcon/MainIcon.js";
import "../../../../../components/styled-tooltip/styled-tooltip.js";
import "../../labels.provider.js";
import "../room-select/room-select.styles.js";
import "@mui/icons-material";
const maskDefaultProps = {
  mask: "00{:}00",
  overwrite: true,
  min: 4
};
const SUB_SELECTING_STATUSES = [AppointmentState.CANCELED, AppointmentState.CONTROLLED];
const AppointmentStatusRadio = ({
  Icon,
  label,
  prefix,
  name,
  disabled,
  isLoading,
  grey = false,
  hasRoomSelector = false,
  onRoomChange,
  onFormChange
}) => {
  const { register, setValue, handleSubmit, watch } = useFormContext();
  const { rooms } = useRooms();
  const watchTime = watch(`${prefix}.time`);
  const watchRoomReferenceId = watch(`roomReferenceId`);
  const debouncedHandleTimeChange = useDebouncedCallback(
    (data, isManualInput) => onFormChange(isManualInput)(data),
    INPUT_DELAY
  );
  const handleTimeChange = useCallback(
    async (value, isManualInput = true) => {
      if (!disabled) {
        setValue(`${prefix}.time`, value);
        await handleSubmit((data) => debouncedHandleTimeChange(data, isManualInput))();
      }
    },
    [handleSubmit, debouncedHandleTimeChange, prefix, setValue, disabled]
  );
  const {
    ref: timeInputRef,
    value: timeValue,
    setValue: setTimeMaskValue
  } = useIMask(maskDefaultProps, {
    onComplete: (value) => handleTimeChange(value)
  });
  useEffect(() => {
    if (watchTime !== timeValue)
      setTimeMaskValue(watchTime ?? "");
  }, [watchTime, setTimeMaskValue]);
  const handleRadioChange = async (event) => {
    if (!event.target.checked || SUB_SELECTING_STATUSES.includes(name))
      return;
    const updatedValue = toTimeString(/* @__PURE__ */ new Date());
    await handleTimeChange(updatedValue, false);
  };
  const handleRoomChange = async (roomReferenceId) => {
    setValue(`roomReferenceId`, roomReferenceId);
    await handleSubmit(onRoomChange(roomReferenceId))();
  };
  return /* @__PURE__ */ jsxs(RootContainer, { grey: grey || disabled, children: [
    /* @__PURE__ */ jsx(
      StatusText,
      {
        label: /* @__PURE__ */ jsxs(LabelContainer, { children: [
          /* @__PURE__ */ jsx(IconContainer, { state: name, children: /* @__PURE__ */ jsx(Icon, {}) }),
          /* @__PURE__ */ jsx(StatusLabel, { variant: "Lato Body 2", grey: grey || disabled, children: label })
        ] }),
        control: /* @__PURE__ */ jsxs("div", { children: [
          isLoading && /* @__PURE__ */ jsx(LoadingCircle, { disableShrink: true }),
          /* @__PURE__ */ jsx(RadioButton, { value: name, grey, disabled, onChange: handleRadioChange })
        ] })
      }
    ),
    /* @__PURE__ */ jsxs(InputGroup, { children: [
      hasRoomSelector && /* @__PURE__ */ jsx(RoomSelect, { value: watchRoomReferenceId, options: rooms, onSelect: handleRoomChange }),
      !SUB_SELECTING_STATUSES.includes(name) && name !== AppointmentState.OPEN && /* @__PURE__ */ jsx(
        StyledTextField,
        {
          ...register(`${prefix}.time`),
          disabled,
          inputRef: timeInputRef,
          variant: "outlined",
          value: timeValue
        }
      )
    ] })
  ] });
};
export {
  AppointmentStatusRadio as default
};
